// Helper script to add initial/existing blogs to Firestore
// Run this once to populate your blog database with existing content

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const initialBlogs = [
    {
        title: "Why We Ignore Health Concerns and Why We Shouldn't",
        excerpt: "Explore the psychological and social factors that lead us to delay seeking medical help, and understand why early intervention matters for your long-term health.",
        content: `Many people tend to ignore health concerns, often dismissing symptoms as minor or temporary. This behavior stems from various psychological and social factors, including fear of diagnosis, busy lifestyles, and cultural stigmas around seeking medical help.

However, early intervention is crucial for long-term health. Ignoring symptoms can lead to complications that could have been easily prevented with timely medical attention. Whether it's a persistent pain, unusual skin changes, or other bodily concerns, addressing them early can make a significant difference in treatment outcomes.

Understanding why we avoid seeking help is the first step toward changing this behavior. By recognizing these patterns, we can make more informed decisions about our health and well-being.`,
        author: "Dr. Nyssa Health Team",
        category: "Health Awareness",
        imageUrl: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        status: "published"
    },
    {
        title: "When Is It Time to See a Doctor for Intimate Health Issues?",
        excerpt: "Learn to recognize the signs and symptoms that indicate it's time to seek professional help for intimate health concerns, without fear or embarrassment.",
        content: `Intimate health issues can be uncomfortable to discuss, but they're important aspects of overall well-being. Many people delay seeking medical help due to embarrassment or fear of judgment, but healthcare professionals are trained to handle these concerns with sensitivity and professionalism.

Signs that you should consult a doctor include persistent pain, unusual discharge, changes in skin texture or color, lumps or bumps, bleeding between periods, or any other symptoms that concern you. Remember, early detection and treatment of intimate health issues can prevent serious complications.

Your health and comfort are paramount. Don't let embarrassment prevent you from getting the care you need. Medical professionals have seen it all and are there to help, not judge.`,
        author: "Dr. Nyssa Health Team",
        category: "Intimate Health",
        imageUrl: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        status: "published"
    },
    {
        title: "Understanding Skin Changes: What's Normal and What's Not",
        excerpt: "Discover how to differentiate between normal skin changes and those that require medical attention, empowering you to take charge of your skin health.",
        content: `Our skin changes throughout our lives due to aging, hormones, sun exposure, and various other factors. While many changes are completely normal, some may indicate underlying health issues that require medical attention.

Normal skin changes include age spots, fine lines, and changes in texture due to aging. However, you should consult a doctor if you notice sudden changes in moles, persistent rashes, unusual growths, changes in skin color, or any lesions that don't heal.

Regular skin self-examinations are important for early detection of potential issues. Pay attention to the ABCDE rule for moles: Asymmetry, Border irregularity, Color variation, Diameter larger than 6mm, and Evolution or change over time.

Taking care of your skin through proper sun protection, hydration, and a healthy lifestyle can help maintain skin health and make it easier to spot when something is amiss.`,
        author: "Dr. Nyssa Health Team",
        category: "Skin Health",
        imageUrl: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        status: "published"
    },
    {
        title: "Post-Pregnancy Body Changes Nobody Talks About",
        excerpt: "An honest discussion about the physical and emotional changes that occur after childbirth, and the support and treatments available to help you feel like yourself again.",
        content: `Pregnancy and childbirth bring profound changes to a woman's body, many of which persist long after delivery. While stretch marks and weight changes are commonly discussed, many other changes remain taboo topics, leaving new mothers feeling isolated and unprepared.

Common post-pregnancy changes include pelvic floor weakness, changes in breast size and shape, skin pigmentation changes, hair loss, and intimate area changes. These are all normal and experienced by many women, yet rarely discussed openly.

It's important to know that help is available. Pelvic floor therapy, cosmetic treatments, and various medical interventions can address many of these concerns. More importantly, talking openly with healthcare providers about these changes is crucial for both physical and emotional well-being.

Remember, your body has done something incredible. While it's natural to want to feel like yourself again, be patient and kind to yourself during this transition. Seek support when needed, and know that you're not alone in experiencing these changes.`,
        author: "Dr. Nyssa Health Team",
        category: "Women's Health",
        imageUrl: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        status: "published"
    },
    {
        title: "How Stress Affects Skin, Hair and Overall Health",
        excerpt: "Understand the profound impact stress has on your body, from hair loss to skin conditions, and learn practical strategies to manage stress for better health.",
        content: `Stress is more than just a mental state—it has tangible effects on your physical health, particularly visible in your skin and hair. When you're stressed, your body releases cortisol and other stress hormones that can trigger various physical responses.

Common stress-related issues include acne breakouts, eczema flare-ups, psoriasis, hair loss (telogen effluvium), premature graying, and accelerated aging signs. Stress can also worsen existing skin conditions and slow down healing processes.

The good news is that managing stress can significantly improve these conditions. Effective stress management techniques include regular exercise, adequate sleep, meditation and mindfulness practices, maintaining social connections, and seeking professional help when needed.

Additionally, maintaining a consistent skincare routine, eating a balanced diet rich in antioxidants, staying hydrated, and protecting your skin from sun damage can help mitigate stress's physical effects.

Remember, taking care of your mental health is just as important as taking care of your physical health. They're intimately connected, and addressing one often helps the other.`,
        author: "Dr. Nyssa Health Team",
        category: "Wellness",
        imageUrl: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        status: "published"
    }
];

const addInitialBlogs = async () => {
    console.log('Starting to add initial blogs to Firestore...');

    try {
        const blogsCollection = collection(db, 'blogs');
        let successCount = 0;
        let errorCount = 0;

        for (const blog of initialBlogs) {
            try {
                const blogData = {
                    ...blog,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };

                await addDoc(blogsCollection, blogData);
                successCount++;
                console.log(`✅ Added: ${blog.title}`);
            } catch (error) {
                errorCount++;
                console.error(`❌ Error adding "${blog.title}":`, error);
            }
        }

        console.log('\n=== Summary ===');
        console.log(`✅ Successfully added: ${successCount} blogs`);
        console.log(`❌ Failed: ${errorCount} blogs`);
        console.log('\nYou can now view these blogs on your website!');

        return { success: successCount, failed: errorCount };
    } catch (error) {
        console.error('Error adding initial blogs:', error);
        throw error;
    }
};

export default addInitialBlogs;
export { initialBlogs };
